import React, { useCallback, useMemo, useState } from 'react'
import { GameDataProviderInterface } from '../../contexts/OwnGamesContextProvider'
import { GameAction, RatingAction, TGameCard } from '../../types/commonTypes'
import { TFunction } from 'i18next'
import { ConfirmationDialog, ConfirmationProps } from '../confirmationDialog/ConfirmationDialog'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { getFocusableGameItemId } from '../GamesOverview/helpers'
import { routes } from '../../routes'
import { DownloadGameForm } from '../../pages/sponsored/components/SponsoredGameDownloadForm'
import { GamePreviewModal } from '../GamePreviewModal/GamePreviewModal'
import { SponsoredDownloadModal } from '../../pages/sponsored/components/SponsoredDownloadModal'
import { LoadingModal } from '../LoadingModal/LoadingModal'
import { useGameNotifications } from '../../hooks/useGameNotifications'
import { GameRatingModal } from '../GameRatingModal/GameRatingModal'
import { useUser } from '../../contexts/userContext'
import { ViewGameRatingsModal } from '../GameRatingModal/ViewGameRatingsModal'
import { useNotification } from '../../contexts/NotificationContext'

type ModalNotificationWrapperChildrenProps = {
  handleGameActionWithSponsoredModal: (action: GameAction, game: TGameCard, sponsorResponse?: SponsorResponse) => void
  handleGameRatingAction: (action: RatingAction, game: TGameCard, text?: string, stars?: number) => void
}

export type ModalNotificationWrapperProps = {
  children: (props: ModalNotificationWrapperChildrenProps) => React.ReactNode
  gameData: GameDataProviderInterface
  handleGameAction: ((action: GameAction, game: TGameCard) => void) | null
  isInstructorView: boolean
}

const getDefaultConfirmationProps = (t: TFunction): ConfirmationProps => ({
  title: t('confirmation_dialog.default_title', 'Warning'),
  text: t('confirmation_dialog.default_text', 'Are you sure you want to proceed?'),
  confirmActionText: t('confirmation_dialog.default_confirm_action_text', 'Confirm'),
  cancelActionText: t('confirmation_dialog.default_cancel_action_text', 'Cancel'),
})

type SponsorResponse = {
  email: string
  loginUrl: string
  msg: string
  product: string
  success: boolean
  errorMessage: string
}

export const ModalNotificationWrapper: React.FC<ModalNotificationWrapperProps> = ({
  children,
  gameData,
  handleGameAction,
  isInstructorView = false,
}) => {
  const [sponsoredGameBeingPreviewed, setSponsoredGameBeingPreviewed] = useState<TGameCard>()
  const [sponsoredGameShownInDownloadModal, setSponsoredGameShownInDownloadModal] = useState<TGameCard>()
  const [gameBeingRated, setGameBeingRated] = useState<TGameCard>()
  const [gameRatingsViewed, setGameRatingsViewed] = useState<TGameCard>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { saveGameRating } = useUser()

  const [showSponsoredContinueConfirmation, setShowSponsoredContinueConfirmation] = useState<boolean>(false)
  const [showSponsoredWrongCodeConfirmation, setShowSponsoredWrongCodeConfirmation] = useState<boolean>(false)
  const [showSponsoredErrorConfirmation, setShowSponsoredErrorConfirmation] = useState<boolean>(false)
  const [showSponsoredLoadingModal, setShowSponsoredLoadingModal] = useState<boolean>(false)
  const [showRatingErrorConfirmation, setShowRatingErrorConfirmation] = useState<boolean>(false)

  const defaultConfirmationProps = useMemo<ConfirmationProps>(() => getDefaultConfirmationProps(t), [t])
  const [confirmationProps, setConfirmationProps] = useState<ConfirmationProps>(defaultConfirmationProps)

  const [loginUrl, setLoginUrl] = useState<string>('')

  const { notifyGameImportStart, notifyGameImportFinish, notifyFetchingGamesFailed } = useGameNotifications()
  const { notifySuccess } = useNotification()

  const showSponsoredContinueConfirmationNewAccountProps = {
    title: t('sponsored_games.download.confirm_redirect_new_account_title', 'Activation succesfull!'),
    text: t(
      'sponsored_games.download.confirm_redirect_new_account_text',
      'Your new Seppo account is all set up, and we’ve sent you a confirmation email with your login details. Log in now to get started with your new game!',
    ),
    confirmActionText: t('sponsored_games.download.confirm_redirect_new_account_confirm_action', 'Go to login'),
    cancelActionText: t('sponsored_games.download.confirm_redirect_new_account_cancel_action', 'Cancel'),
  }

  const showSponsoredContinueConfirmationProps = {
    title: t('sponsored_games.download.confirm_redirect_title', 'Activation succesfull!'),
    text: t(
      'sponsored_games.download.confirm_redirect_text',
      'Your game has been successfully added to your existing Seppo account. We´ve sent you a confirmation email with all the details. Ready to log in now?',
    ),
    confirmActionText: t('sponsored_games.download.confirm_redirect_confirm_action', 'Go to login'),
    cancelActionText: t('sponsored_games.download.confirm_redirect_cancel_action', 'Cancel'),
  }

  const showSponsoredWrongCodeConfirmationProps = {
    title: t('sponsored_games.download.confirm_wrong_code_title', 'Activation code incorrect!'),
    text: t('sponsored_games.download.confirm_wrong_code_text', 'The code you gave was incorrect.'),
    confirmActionText: t('sponsored_games.download.confirm_wrong_code_confirm_action', 'OK'),
  }

  const showSponsoredErrorConfirmationProps = {
    title: t('sponsored_games.download.confirm_error_title', 'Something went wrong!'),
    text: t(
      'sponsored_games.download.confirm_error_text',
      'Something went wrong while activating the game. Please try again or contact customer support if problem persists.',
    ),
    confirmActionText: t('sponsored_games.download.confirm_error_confirm_action', 'OK'),
  }

  const showRatingErrorConfirmationProps = {
    title: t('rating.save.error.title', 'Game rating not saved'),
    text: t('rating.save.error.content', 'Something went wrong and your game rating was not saved'),
    confirmActionText: t('rating.save.error.confirm_action', 'OK'),
  }

  const handleGameActionWithSponsoredModal = (
    action: GameAction,
    game: TGameCard,
    sponsorResponse?: SponsorResponse,
  ) => {
    if (game.librarySource === 'SPONSORED') {
      if (action === GameAction.VIEW) {
        setSponsoredGameBeingPreviewed(game)
      } else if (action === GameAction.DOWNLOADMODAL) {
        setSponsoredGameShownInDownloadModal(game)
      } else if (action === GameAction.DOWNLOAD) {
        handleSponsoredGameDownload(!isInstructorView, sponsorResponse)
      } else if (action === GameAction.RATE) {
        handleGameRatingAction(RatingAction.VIEW, game)
      } else if (action === GameAction.VIEWRATE) {
        handleGameRatingAction(RatingAction.OPEN, game)
      } else if (action === GameAction.CLOSEVIEW) {
        if (sponsoredGameBeingPreviewed) {
          setSponsoredGameBeingPreviewed(undefined)
        }
      }
    } else if (action === GameAction.RATE) {
      handleGameRatingAction(RatingAction.VIEW, game)
    } else if (action === GameAction.VIEWRATE) {
      handleGameRatingAction(RatingAction.OPEN, game)
    } else {
      if (handleGameAction) handleGameAction(action, game)
    }
  }

  const handleSponsoredGameDownload = (showConfirmation: boolean, sponsorResponse?: SponsorResponse) => {
    if (sponsorResponse && sponsorResponse.success && sponsorResponse.msg === 'OK') {
      setLoginUrl(sponsorResponse?.loginUrl)
      setShowSponsoredLoadingModal(false)
      if (showConfirmation) {
        if (sponsorResponse.loginUrl.trim() === '/') {
          setConfirmationProps(showSponsoredContinueConfirmationProps)
        } else {
          setConfirmationProps(showSponsoredContinueConfirmationNewAccountProps)
        }
        setShowSponsoredContinueConfirmation(true)
      }
    } else if (sponsorResponse && sponsorResponse.success && sponsorResponse.msg === 'WRONG_CODE') {
      setShowSponsoredLoadingModal(false)
      if (showConfirmation) {
        setConfirmationProps(showSponsoredWrongCodeConfirmationProps)
        setShowSponsoredWrongCodeConfirmation(true)
      }
    } else {
      setShowSponsoredLoadingModal(false)
      if (showConfirmation) {
        setConfirmationProps(showSponsoredErrorConfirmationProps)
        setShowSponsoredErrorConfirmation(true)
      }
    }
    if (!showConfirmation) {
      handleClosePreviewModal()
      handleCloseDownloadModal()
    }
  }

  const handleClosePreviewModal = () => {
    if (sponsoredGameBeingPreviewed == null) {
      return
    }
    const gameId = sponsoredGameBeingPreviewed.id
    setSponsoredGameBeingPreviewed(undefined)
    document.querySelector<any>(`#${getFocusableGameItemId(gameId)}`)?.focus?.()
  }

  const handleCloseDownloadModal = () => {
    if (sponsoredGameShownInDownloadModal == null) {
      return
    }
    const gameId = sponsoredGameShownInDownloadModal.id
    setSponsoredGameShownInDownloadModal(undefined)
    document.querySelector<any>(`#${getFocusableGameItemId(gameId)}`)?.focus?.()
  }

  const resolveConfirmation = useCallback(
    (answer: boolean) => {
      setShowSponsoredContinueConfirmation(false)
      setConfirmationProps(defaultConfirmationProps)
      if (answer) {
        if (loginUrl.trim() === '/' || loginUrl.trim() === '') {
          navigate(routes.login)
        } else {
          navigate(loginUrl)
        }
      }
      if (sponsoredGameShownInDownloadModal) setSponsoredGameShownInDownloadModal(undefined)
      if (sponsoredGameBeingPreviewed) setSponsoredGameBeingPreviewed(undefined)
      setLoginUrl('')
    },
    [defaultConfirmationProps, sponsoredGameShownInDownloadModal, sponsoredGameBeingPreviewed, loginUrl, navigate],
  )

  const resolveWrongCodeConfirmation = useCallback(
    (answer: boolean) => {
      setShowSponsoredWrongCodeConfirmation(false)
      setConfirmationProps(defaultConfirmationProps)
    },
    [defaultConfirmationProps],
  )

  const resolveErrorConfirmation = useCallback(
    (answer: boolean) => {
      setShowSponsoredErrorConfirmation(false)
      setConfirmationProps(defaultConfirmationProps)
    },
    [defaultConfirmationProps],
  )

  const GameActivation = async (formProps: DownloadGameForm, game: TGameCard) => {
    if (!isInstructorView) {
      setShowSponsoredLoadingModal(true)
    }
    const { sponsorCode, email, organization } = formProps
    let notifyId: string = ''
    if (gameData.activateGame) {
      if (isInstructorView) {
        notifyId = notifyGameImportStart(game.gameName)
      }
      const response = await gameData.activateGame(email, organization, sponsorCode, game.id)
      if (response && response.success && response.value && response.value.msg === 'OK') {
        const newSponsorResponse: SponsorResponse = {
          email: response?.value?.email ?? '',
          loginUrl: response?.value?.login_redir_url ?? '',
          msg: response?.value?.msg ?? '',
          product: response?.value?.product_row ?? '',
          success: response?.success,
          errorMessage: '',
        }
        handleGameActionWithSponsoredModal(GameAction.DOWNLOAD, game, newSponsorResponse)
        if (isInstructorView && notifyId) {
          notifyGameImportFinish(notifyId, game.gameName, true)
        }
      } else if ((response && !response.success) || (response && response.value && response.value.msg !== 'OK')) {
        const newSponsorResponse: SponsorResponse = {
          email: '',
          loginUrl: '',
          msg: '',
          product: '',
          success: response?.success,
          errorMessage: !response.success ? response?.error.toString() : response.value.msg,
        }
        if (isInstructorView && notifyId) {
          notifyGameImportFinish(notifyId, game.gameName, false)
        }
        handleGameActionWithSponsoredModal(GameAction.DOWNLOAD, game, newSponsorResponse)
      } else {
        const newSponsorResponse: SponsorResponse = {
          email: '',
          loginUrl: '',
          msg: '',
          product: '',
          success: false,
          errorMessage: 'Unknown error',
        }
        if (isInstructorView && notifyId) {
          notifyFetchingGamesFailed()
        }
        handleGameActionWithSponsoredModal(GameAction.DOWNLOAD, game, newSponsorResponse)
      }
      return response
    }
    if (isInstructorView && notifyId) {
      notifyFetchingGamesFailed()
    }
    return null
  }

  const handleGameRatingAction = (action: RatingAction, game?: TGameCard, text?: string, stars?: number) => {
    switch (action) {
      case RatingAction.VIEW:
        if (game) setGameBeingRated(game)
        return
      case RatingAction.SEND:
        if (gameBeingRated && gameBeingRated.conlibGameId && text && stars) {
          handleSaveGameRating(text, stars)
        }
        return
      case RatingAction.OPEN:
        setGameRatingsViewed(game)
        return
      default:
        return
    }
  }

  const handleSaveGameRating = async (text: string | null, stars: number | null) => {
    if (gameBeingRated && gameBeingRated.conlibGameId && text != null && stars != null) {
      saveGameRating(gameBeingRated.id.toString(), stars, text).then((response) => {
        if (response) {
          notifySuccess({
            title: t('rating.save.success.title', 'Game rating saved'),
            content: t('rating.save.success.content', 'Data saved succesfully'),
          })
          gameData.refreshGames()
          setGameBeingRated(undefined)
        } else {
          setConfirmationProps(showRatingErrorConfirmationProps)
          setShowRatingErrorConfirmation(true)
        }
      })
    }
  }

  const resolveRatingErrorConfirmation = useCallback(
    (answer: boolean) => {
      setShowRatingErrorConfirmation(false)
      setConfirmationProps(defaultConfirmationProps)
    },
    [defaultConfirmationProps],
  )

  const internalOnSaveCloseRating = () => {
    if (gameBeingRated) handleGameActionWithSponsoredModal(GameAction.CLOSEVIEW, gameBeingRated)
    setGameBeingRated(undefined)
  }

  return (
    <>
      {children({ handleGameActionWithSponsoredModal, handleGameRatingAction })}
      {sponsoredGameBeingPreviewed != null && (
        <GamePreviewModal
          game={sponsoredGameBeingPreviewed}
          onClose={handleClosePreviewModal}
          onGameAction={handleGameActionWithSponsoredModal}
          isSponsored={true}
          sponsorCode={gameData.filters?.activationCode}
        />
      )}
      {sponsoredGameShownInDownloadModal != null && (
        <SponsoredDownloadModal
          game={sponsoredGameShownInDownloadModal}
          onClose={handleCloseDownloadModal}
          activateGame={GameActivation}
          sponsorCode={gameData.filters?.activationCode}
          isInstructorView={isInstructorView}
        />
      )}
      {gameBeingRated && (
        <GameRatingModal
          game={gameBeingRated}
          onClose={() => setGameBeingRated(undefined)}
          saveGameRating={handleSaveGameRating}
          onSaveClose={internalOnSaveCloseRating}
        />
      )}
      {gameRatingsViewed && (
        <ViewGameRatingsModal game={gameRatingsViewed} onClose={() => setGameRatingsViewed(undefined)} />
      )}
      <ConfirmationDialog
        show={showSponsoredContinueConfirmation}
        confirmationProps={confirmationProps}
        resolveConfirmation={resolveConfirmation}
      />
      <ConfirmationDialog
        show={showSponsoredWrongCodeConfirmation}
        confirmationProps={confirmationProps}
        resolveConfirmation={resolveWrongCodeConfirmation}
        showCancelButton={false}
      />
      <ConfirmationDialog
        show={showSponsoredErrorConfirmation}
        confirmationProps={confirmationProps}
        resolveConfirmation={resolveErrorConfirmation}
        showCancelButton={false}
      />
      <ConfirmationDialog
        show={showRatingErrorConfirmation}
        confirmationProps={confirmationProps}
        resolveConfirmation={resolveRatingErrorConfirmation}
        showCancelButton={false}
      />
      <LoadingModal show={showSponsoredLoadingModal} />
    </>
  )
}
